import type { MouseEvent } from "react";

import { SWReactCountryIcons } from "@similarweb/icons";

import { EmptyOverlayContent } from "app/pages/activation/components/HomeWithData/ShareData/TrafficWidgets/RankTracker/BodyByState/EmptyOverlayContent";
import { getEmptyState } from "app/pages/activation/components/HomeWithData/ShareData/TrafficWidgets/RankTracker/helpers/getEmptyState";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { generateAutomationName } from "app/pages/activation/helpers/generateAutomationName";
import {
    ACTIVATION_PROJECT_ROUTE,
    DEFAULT_ACTIVATION_GRANULARITY,
    DEFAULT_ACTIVATION_WEB_SOURCE,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getTrafficWidgetState } from "../../utils";
import { TrafficWidgetLayout } from "../TrafficWidgetLayout";
import type { IRankTrackerProps } from "../types";

import Default from "./BodyByState/Default";
import Empty from "./BodyByState/Empty";
import { Loading } from "./BodyByState/Loading";
import { RankTrackerRightAside } from "./RightAside";
import { TITLE, TOOLTIP } from "./texts";

export const RankTracker = ({
    data,
    isLoading,
    isLocked,
    isEmpty,
    onCreateProject,
    hasProjects,
    hasTopics,
    isScraping,
}: IRankTrackerProps) => {
    const { activeActivation } = useHomeContext();
    const state = getTrafficWidgetState({ isEmpty, isLoading, isLocked });
    const isOwn = hasProjects ? (activeActivation?.isOwn ?? true) : true;
    const emptyState = getEmptyState(hasProjects, hasTopics, isScraping);
    const trackingApi = activationTracking.homePageWithData;

    const onRedirectToTopics = async () => {
        if (!activeActivation?.id) {
            return;
        }

        await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: activeActivation.id,
            country: activeActivation.country,
            webSource: DEFAULT_ACTIVATION_WEB_SOURCE,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            shouldScrollToTopics: true,
        });
    };

    const onRedirectToRTWidget = async () => {
        if (!activeActivation?.id) {
            return;
        }

        await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            key: activeActivation.id,
            country: activeActivation.country,
            webSource: DEFAULT_ACTIVATION_WEB_SOURCE,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            shouldScrollToRT: true,
        });
    };

    const onClick = async (event: MouseEvent) => {
        event.preventDefault();

        if (state === "default") {
            trackingApi.trackClickRankTracker("view project");

            await onRedirectToRTWidget();
            return;
        }

        if (state === "empty") {
            if (emptyState === "noProject") {
                trackingApi.trackClickRankTracker("create a new project");

                onCreateProject();
                return;
            }
            if (emptyState === "noTopics") {
                trackingApi.trackClickRankTracker("create topics");

                await onRedirectToTopics();
                return;
            }
            if (emptyState === "noRankTracker") {
                trackingApi.trackClickRankTracker("create rank tracker");

                await onRedirectToRTWidget();
            }
        }
    };

    return (
        <TrafficWidgetLayout
            titleKey={TITLE}
            onClick={activeActivation?.id ? onClick : undefined}
            tooltipKey={TOOLTIP}
            state={state}
            dataAutomation={generateAutomationName("home", ["share", "data", "rank", "tracker"])}
            titleSupplement={
                data?.country != null ? (
                    <SWReactCountryIcons
                        countryCode={data.country}
                        size="14px"
                    />
                ) : undefined
            }
            bodyByState={{
                default: data ? <Default data={data} /> : null,
                empty: <Empty />,
                loading: <Loading />,
            }}
            rightAsideByState={{
                default: <RankTrackerRightAside data={data ?? null} />,
                empty: <RankTrackerRightAside isEmpty />,
                loading: <RankTrackerRightAside isLoading />,
            }}
            emptyStateOverlay={
                state === "empty" ? (
                    <EmptyOverlayContent
                        emptyState={emptyState}
                        onClick={(e) => void onClick(e)}
                        isOwn={isOwn}
                    />
                ) : undefined
            }
        />
    );
};
